import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RegionCode } from 'google-libphonenumber';
import { UpdateVendorFormProps } from '../../../interfaces';
import { ExtendedUseFormReturn } from '../../../../../hooks/providers/useFormHooks';
import { useGetSupportedCountries } from './useGetSupportedCountries';
import { ISupportedCountry } from '../../../../MoveMoney/types';
import { generateCountryPickerData } from '../../../services';
import { useGetSwiftData } from '../../../../MoveMoney/hooks/useGetSwiftData';
import { UpdateVendorFormFieldsMap } from '../../../../MoveMoney/form/updateVendorFormFieldsMap';
import { removeSpacesFromString } from '../../../../../services/general.service';
import { getIbanValidationData } from '../../../../MoveMoney/api/getIbanValidationData';
import { IServerErrorResponse } from '../../../../../types';
import { FormCtx } from '../../../pages/UpdateVendorFormContainer';

interface IUseInternationalBaseFieldLogic {
  formMethods: ExtendedUseFormReturn<UpdateVendorFormProps>
  dynamicCountry: string;
}

export const useInternationalBaseFieldLogic = ({ formMethods, dynamicCountry }: IUseInternationalBaseFieldLogic) => {
  const shouldAvoidSwiftFetch = useRef<boolean>(false);
  const isAutoCountrySelectEnabled = useRef<boolean>(true);
  const [isPhoneError, setIsPhoneError] = useState<boolean>(false);
  const [isOtherBusinessFlow, setIsOtherBusinessFlow] = useState<boolean>(false);
  const ctx = useContext(FormCtx);

  const { data: supportedCountries } = useGetSupportedCountries();
  const { mutateAsync: getSwiftBankData, isLoading: isLoadingSwiftData, data: swiftData, reset: resetSwiftData } = useGetSwiftData(
    (errMsg: string) => {
      formMethods.setError(UpdateVendorFormFieldsMap.SWIFT, { message: errMsg });
    },
  );
  const { mutateAsync: getIbanData, error: ibanErrorData, data: ibanData } = getIbanValidationData();

  const selectedCountryData: ISupportedCountry | undefined = useMemo(() => supportedCountries?.supportedCountries.countries.find((country) => country.code === dynamicCountry), [dynamicCountry]);
  const supportedCountriesList = supportedCountries ? generateCountryPickerData(supportedCountries) : [];

  const handleSwiftBlur = async (swiftValue: string, errMessage: string) => {
    if (shouldAvoidSwiftFetch.current) {
      shouldAvoidSwiftFetch.current = false;
      return;
    }
    if (!errMessage && swiftValue && (swiftValue !== swiftData?.usedSwiftCode)) {
      await getSwiftBankData(swiftValue);
    }
  };

  const shouldValidateIban = () => {
    const valueToParse = selectedCountryData?.ibanSpecificValidationRequired || 'false';
    try {
      return JSON.parse(valueToParse);
    } catch (error) {
      console.error('Error in shouldValidateIban', error);
      return false;
    }
  };

  const handleIbanBlur = async (ibanValue: string, errMessage: string) => {
    if (ibanValue && shouldValidateIban() && dynamicCountry && ibanValue !== ibanData?.usedIbanCode) {
      if (!errMessage) {
        getIbanData(removeSpacesFromString(ibanValue));
      }
    }
  };

  const handleIsoCodeChange = (isoCode: RegionCode) => {
    formMethods.setValue(UpdateVendorFormFieldsMap.INTER_PHONE_REGION_CODE, isoCode);
  };

  const handlePhoneBlur = () => {
    const isPhoneValueInvalid = formMethods.getFieldState(UpdateVendorFormFieldsMap.INTER_PHONE_STATUS).invalid;
    setIsPhoneError(isPhoneValueInvalid);
  };

  const isCountryMissMatch = () => swiftData?.countryCode && dynamicCountry && dynamicCountry !== 'OTHER' && dynamicCountry !== swiftData.countryCode;

  const isCountryFieldClean = () => !dynamicCountry && !formMethods.getFieldState(UpdateVendorFormFieldsMap.INTER_COUNTRY).isTouched;

  const shouldSetCountryAutomatically = () => swiftData && swiftData?.countryCode && !dynamicCountry && isAutoCountrySelectEnabled.current;

  useEffect(() => {
    const err = ibanErrorData as IServerErrorResponse;
    if (err?.response?.data?.errors?.length) {
      formMethods.setError(UpdateVendorFormFieldsMap.IBAN, { message: err?.response?.data?.errors?.[0]?.localizedMessage || 'Please enter a valid IBAN' });
    }
  }, [ibanErrorData]);

  useEffect(() => {
    if (ibanData?.country_code && dynamicCountry && dynamicCountry !== ibanData.country_code) {
      formMethods.setError(UpdateVendorFormFieldsMap.IBAN, { message: 'IBAN Code does not match the selected country' });
    }
  }, [ibanData]);

  useEffect(() => {
    if (dynamicCountry === 'OTHER') {
      setIsOtherBusinessFlow(true);
      isAutoCountrySelectEnabled.current = false;
      formMethods.resetField(UpdateVendorFormFieldsMap.INTER_COUNTRY);
      setTimeout(() => {
        formMethods.clearErrors(UpdateVendorFormFieldsMap.INTER_COUNTRY);
        isAutoCountrySelectEnabled.current = true;
      }, 1500);
      return;
    }
    if (shouldSetCountryAutomatically()) {
      formMethods.setValue(UpdateVendorFormFieldsMap.INTER_COUNTRY, swiftData!.countryCode);
    }
    if (isCountryFieldClean()) return;
    if (isCountryMissMatch()) {
      formMethods.setError(UpdateVendorFormFieldsMap.SWIFT, { message: 'SWIFT Code does not match the selected country' });
    } else if (formMethods.getValues(UpdateVendorFormFieldsMap.SWIFT)) {
      formMethods.trigger(UpdateVendorFormFieldsMap.SWIFT);
    }

    if (formMethods.getValues(UpdateVendorFormFieldsMap.INTER_POSTAL_CODE)) {
      formMethods.trigger(UpdateVendorFormFieldsMap.INTER_POSTAL_CODE);
    }
  }, [swiftData, dynamicCountry, ctx.scheme]);

  useEffect(() => {
    if (formMethods.getValues(UpdateVendorFormFieldsMap.IBAN)) {
      formMethods.resetField(UpdateVendorFormFieldsMap.IBAN);
      setTimeout(() => {
        formMethods.clearErrors(UpdateVendorFormFieldsMap.IBAN);
      }, 200);
    }
  }, [dynamicCountry]);

  useEffect(() => {
    const combobox: HTMLInputElement | null = document.querySelector('[role="combobox"]');
    if (combobox) {
      combobox.addEventListener('focus', (e) => {
        // @ts-ignore
        e?.target?.setAttribute('autocomplete', 'new-password');
      });
    }

    resetSwiftData();
  }, []);

  return {
    selectedCountryData,
    isLoadingSwiftData,
    supportedCountriesList,
    getSwiftBankData,
    swiftData,
    resetSwiftData,
    handleSwiftBlur,
    shouldAvoidSwiftFetch,
    isAutoCountrySelectEnabled,
    handleIsoCodeChange,
    handlePhoneBlur,
    isPhoneError,
    setIsPhoneError,
    handleIbanBlur,
    isOtherBusinessFlow,
    setIsOtherBusinessFlow,
  };
};
